import { useState, useEffect, useMemo } from 'react';
import {
  retirementService,
  type ApproachingRetirement,
  type RetirementCase,
  type RetirementStats,
  type RetirementChecklistItem,
  type ExitClearanceItem,
} from '@/services/retirement';
import styles from './RetirementProcessingPage.module.css';

type TabType = 'alerts' | 'active' | 'completed';

export function RetirementProcessingPage() {
  const [activeTab, setActiveTab] = useState<TabType>('alerts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [stats, setStats] = useState<RetirementStats | null>(null);
  const [approaching, setApproaching] = useState<ApproachingRetirement[]>([]);
  const [activeCases, setActiveCases] = useState<RetirementCase[]>([]);
  const [completedCases, setCompletedCases] = useState<RetirementCase[]>([]);

  // Filters
  const [alertFilter, setAlertFilter] = useState<string>('all');
  const [caseFilter, setCaseFilter] = useState<string>('all');

  // Detail view
  const [selectedCase, setSelectedCase] = useState<RetirementCase | null>(null);
  const [checklist, setChecklist] = useState<RetirementChecklistItem[]>([]);
  const [clearance, setClearance] = useState<ExitClearanceItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create case modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<ApproachingRetirement | null>(null);

  // Load initial data
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [statsRes, approachingRes] = await Promise.all([
          retirementService.getStats(),
          retirementService.getApproaching(6),
        ]);

        if (statsRes.success && statsRes.data) setStats(statsRes.data);
        if (approachingRes.success && approachingRes.data) setApproaching(approachingRes.data);
      } catch {
        setError('Failed to load retirement data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Load tab-specific data
  useEffect(() => {
    async function loadTabData() {
      if (activeTab === 'active') {
        const response = await retirementService.listCases({ perPage: 50 });
        if (response.success && response.data) {
          setActiveCases(response.data.filter(c => c.status !== 'completed' && c.status !== 'cancelled'));
        }
      } else if (activeTab === 'completed') {
        const response = await retirementService.listCases({ perPage: 50, status: 'completed' });
        if (response.success && response.data) {
          setCompletedCases(response.data);
        }
      }
    }
    loadTabData();
  }, [activeTab]);

  // Load case details
  useEffect(() => {
    async function loadCaseDetails() {
      if (!selectedCase) {
        setChecklist([]);
        setClearance([]);
        return;
      }
      setDetailLoading(true);
      try {
        const [checklistRes, clearanceRes] = await Promise.all([
          retirementService.getChecklist(selectedCase.id),
          retirementService.getClearance(selectedCase.id),
        ]);
        if (checklistRes.success && checklistRes.data) setChecklist(checklistRes.data);
        if (clearanceRes.success && clearanceRes.data) setClearance(clearanceRes.data);
      } catch {
        // Ignore errors
      } finally {
        setDetailLoading(false);
      }
    }
    loadCaseDetails();
  }, [selectedCase]);

  // Filtered data
  const filteredApproaching = useMemo(() => {
    if (alertFilter === 'all') return approaching;
    if (alertFilter === 'urgent') return approaching.filter(a => a.daysUntilRetirement <= 30);
    if (alertFilter === 'no_case') return approaching.filter(a => !a.hasActiveCase);
    return approaching.filter(a => a.retirementType === alertFilter);
  }, [approaching, alertFilter]);

  const filteredCases = useMemo(() => {
    const cases = activeTab === 'completed' ? completedCases : activeCases;
    if (caseFilter === 'all') return cases;
    return cases.filter(c => c.status === caseFilter || c.retirementType === caseFilter);
  }, [activeCases, completedCases, activeTab, caseFilter]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getUrgencyClass = (days: number) => {
    if (days <= 30) return styles.urgencyCritical;
    if (days <= 90) return styles.urgencyWarning;
    return styles.urgencyNormal;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Pending',
      documents_pending: 'Documents Pending',
      documents_verified: 'Documents Verified',
      pension_submitted: 'Pension Submitted',
      pension_processing: 'Pension Processing',
      clearance_pending: 'Clearance Pending',
      clearance_completed: 'Clearance Done',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
      pending: styles.statusPending,
      documents_pending: styles.statusDocPending,
      documents_verified: styles.statusDocVerified,
      pension_submitted: styles.statusPensionSub,
      pension_processing: styles.statusPensionProc,
      clearance_pending: styles.statusClearPending,
      clearance_completed: styles.statusClearDone,
      completed: styles.statusCompleted,
      cancelled: styles.statusCancelled,
    };
    return classes[status] || '';
  };

  const getRetirementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      mandatory: 'Mandatory',
      voluntary: 'Voluntary',
      medical: 'Medical',
      death_in_service: 'Death in Service',
      compulsory: 'Compulsory',
    };
    return labels[type] || type;
  };

  const getChecklistProgress = () => {
    if (checklist.length === 0) return 0;
    const completed = checklist.filter(i => i.status === 'verified' || i.status === 'waived').length;
    return Math.round((completed / checklist.length) * 100);
  };

  const getClearanceProgress = () => {
    if (clearance.length === 0) return 0;
    const cleared = clearance.filter(i => i.status === 'cleared').length;
    return Math.round((cleared / clearance.length) * 100);
  };

  const tabs: { id: TabType; label: string; icon: string; badge?: number }[] = [
    { id: 'alerts', label: 'Retirement Alerts', icon: '🔔', badge: approaching.filter(a => !a.hasActiveCase).length },
    { id: 'active', label: 'Active Cases', icon: '📋', badge: stats?.pending || 0 },
    { id: 'completed', label: 'Completed', icon: '✅' },
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading retirement data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Retirement Processing</h1>
          <p className={styles.subtitle}>
            Pre-retirement alerts, document verification, and clearance management
          </p>
        </div>
        <div className={styles.headerActions}>
          {activeTab === 'alerts' && (
            <button className={styles.primaryBtn} onClick={() => setShowCreateModal(true)}>
              <span className={styles.btnIcon}>+</span>
              Create Case
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className={styles.errorBanner}>
          <span>Error: {error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Stats Overview */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats?.approachingCount || approaching.length}</span>
          <span className={styles.statLabel}>Approaching</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats?.documentsPending || 0}</span>
          <span className={styles.statLabel}>Docs Pending</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats?.pensionProcessing || 0}</span>
          <span className={styles.statLabel}>Pension Processing</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats?.clearancePending || 0}</span>
          <span className={styles.statLabel}>Clearance Pending</span>
        </div>
        <div className={styles.statItem}>
          <span className={`${styles.statValue} ${styles.statCompleted}`}>{stats?.completed || 0}</span>
          <span className={styles.statLabel}>Completed</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className={styles.tabBadge}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* List Panel */}
        <div className={styles.listPanel}>
          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <>
              <div className={styles.filterBar}>
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Filter</label>
                  <select
                    className={styles.filterSelect}
                    value={alertFilter}
                    onChange={e => setAlertFilter(e.target.value)}
                  >
                    <option value="all">All Staff</option>
                    <option value="urgent">Urgent (30 days)</option>
                    <option value="no_case">No Case Created</option>
                    <option value="mandatory">Mandatory</option>
                    <option value="voluntary">Voluntary</option>
                  </select>
                </div>
                <span className={styles.filterCount}>
                  {filteredApproaching.length} staff approaching retirement
                </span>
              </div>

              <div className={styles.alertList}>
                {filteredApproaching.map(staff => (
                  <div
                    key={staff.staffId}
                    className={`${styles.alertCard} ${getUrgencyClass(staff.daysUntilRetirement)}`}
                    onClick={() => setSelectedStaff(staff)}
                  >
                    <div className={styles.alertCardHeader}>
                      <div className={styles.staffInfo}>
                        <span className={styles.staffName}>
                          {staff.firstName} {staff.lastName}
                        </span>
                        <span className={styles.staffMeta}>
                          {staff.employeeId} • {staff.departmentName || 'No Department'}
                        </span>
                      </div>
                      <div className={styles.alertBadges}>
                        <span className={`${styles.daysBadge} ${getUrgencyClass(staff.daysUntilRetirement)}`}>
                          {staff.daysUntilRetirement} days
                        </span>
                        {staff.hasActiveCase ? (
                          <span className={styles.hasCaseBadge}>Case Active</span>
                        ) : (
                          <span className={styles.noCaseBadge}>No Case</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.alertCardBody}>
                      <div className={styles.alertDetail}>
                        <span className={styles.detailIcon}>📅</span>
                        Retirement: {formatDate(staff.expectedRetirementDate)}
                      </div>
                      <div className={styles.alertDetail}>
                        <span className={styles.detailIcon}>📋</span>
                        Type: {getRetirementTypeLabel(staff.retirementType)}
                      </div>
                      {staff.jobTitle && (
                        <div className={styles.alertDetail}>
                          <span className={styles.detailIcon}>💼</span>
                          {staff.jobTitle}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {filteredApproaching.length === 0 && (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyIcon}>🎉</span>
                    <p>No staff approaching retirement in the selected filter</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Active/Completed Cases Tab */}
          {(activeTab === 'active' || activeTab === 'completed') && (
            <>
              <div className={styles.filterBar}>
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Filter</label>
                  <select
                    className={styles.filterSelect}
                    value={caseFilter}
                    onChange={e => setCaseFilter(e.target.value)}
                  >
                    <option value="all">All Cases</option>
                    {activeTab === 'active' && (
                      <>
                        <option value="pending">Pending</option>
                        <option value="documents_pending">Documents Pending</option>
                        <option value="pension_processing">Pension Processing</option>
                        <option value="clearance_pending">Clearance Pending</option>
                      </>
                    )}
                    <option value="mandatory">Mandatory</option>
                    <option value="voluntary">Voluntary</option>
                    <option value="medical">Medical</option>
                  </select>
                </div>
                <span className={styles.filterCount}>
                  {filteredCases.length} cases
                </span>
              </div>

              <div className={styles.caseList}>
                {filteredCases.map(caseItem => (
                  <div
                    key={caseItem.id}
                    className={`${styles.caseCard} ${selectedCase?.id === caseItem.id ? styles.caseSelected : ''}`}
                    onClick={() => setSelectedCase(caseItem)}
                  >
                    <div className={styles.caseCardHeader}>
                      <div className={styles.staffInfo}>
                        <span className={styles.staffName}>
                          {caseItem.staff.firstName} {caseItem.staff.lastName}
                        </span>
                        <span className={styles.caseNumber}>{caseItem.caseNumber}</span>
                      </div>
                      <span className={`${styles.statusBadge} ${getStatusClass(caseItem.status)}`}>
                        {getStatusLabel(caseItem.status)}
                      </span>
                    </div>
                    <div className={styles.caseCardBody}>
                      <div className={styles.caseDetail}>
                        <span className={styles.detailIcon}>📅</span>
                        {formatDate(caseItem.expectedRetirementDate)}
                      </div>
                      <div className={styles.caseDetail}>
                        <span className={styles.detailIcon}>📋</span>
                        {getRetirementTypeLabel(caseItem.retirementType)}
                      </div>
                    </div>
                    <div className={styles.caseProgress}>
                      <div className={styles.progressItem}>
                        <span className={caseItem.checklistCompleted ? styles.progressDone : ''}>
                          {caseItem.checklistCompleted ? '✓' : '○'} Docs
                        </span>
                      </div>
                      <div className={styles.progressItem}>
                        <span className={caseItem.pensionStatus !== 'not_submitted' ? styles.progressDone : ''}>
                          {caseItem.pensionStatus !== 'not_submitted' ? '✓' : '○'} Pension
                        </span>
                      </div>
                      <div className={styles.progressItem}>
                        <span className={caseItem.clearanceCompleted ? styles.progressDone : ''}>
                          {caseItem.clearanceCompleted ? '✓' : '○'} Clearance
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredCases.length === 0 && (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyIcon}>📋</span>
                    <p>No cases found</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Detail Panel */}
        {selectedCase && (
          <div className={styles.detailPanel}>
            {detailLoading ? (
              <div className={styles.detailLoading}>
                <div className={styles.spinner} />
              </div>
            ) : (
              <>
                <div className={styles.detailHeader}>
                  <div>
                    <h2 className={styles.detailTitle}>
                      {selectedCase.staff.firstName} {selectedCase.staff.lastName}
                    </h2>
                    <p className={styles.detailSubtitle}>
                      {selectedCase.caseNumber} • {getRetirementTypeLabel(selectedCase.retirementType)}
                    </p>
                  </div>
                  <button className={styles.closeDetail} onClick={() => setSelectedCase(null)}>
                    &times;
                  </button>
                </div>

                <div className={styles.detailBody}>
                  {/* Staff Info */}
                  <div className={styles.infoSection}>
                    <h3 className={styles.sectionTitle}>Staff Information</h3>
                    <div className={styles.infoGrid}>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Employee ID</span>
                        <span className={styles.infoValue}>{selectedCase.staff.employeeId || '—'}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Email</span>
                        <span className={styles.infoValue}>{selectedCase.staff.email}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Department</span>
                        <span className={styles.infoValue}>{selectedCase.staff.department?.name || '—'}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Retirement Date</span>
                        <span className={styles.infoValue}>{formatDate(selectedCase.expectedRetirementDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Document Checklist */}
                  <div className={styles.checklistSection}>
                    <div className={styles.sectionHeader}>
                      <h3 className={styles.sectionTitle}>Document Checklist</h3>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${getChecklistProgress()}%` }}
                        />
                        <span className={styles.progressText}>{getChecklistProgress()}%</span>
                      </div>
                    </div>
                    <div className={styles.checklistItems}>
                      {checklist.map(item => (
                        <div
                          key={item.id}
                          className={`${styles.checklistItem} ${styles[`checklist${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`]}`}
                        >
                          <div className={styles.checklistIcon}>
                            {item.status === 'verified' && '✓'}
                            {item.status === 'waived' && '~'}
                            {item.status === 'rejected' && '✗'}
                            {item.status === 'submitted' && '○'}
                            {item.status === 'pending' && '□'}
                          </div>
                          <div className={styles.checklistInfo}>
                            <span className={styles.checklistName}>{item.itemName}</span>
                            {item.isRequired && <span className={styles.requiredBadge}>Required</span>}
                          </div>
                          <span className={`${styles.checklistStatus} ${styles[`status${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`]}`}>
                            {item.status}
                          </span>
                        </div>
                      ))}
                      {checklist.length === 0 && (
                        <p className={styles.noItems}>No checklist items</p>
                      )}
                    </div>
                  </div>

                  {/* Pension Status */}
                  <div className={styles.pensionSection}>
                    <h3 className={styles.sectionTitle}>Pension Processing</h3>
                    <div className={styles.pensionStatus}>
                      <div className={styles.pensionStep}>
                        <span className={`${styles.pensionDot} ${selectedCase.pensionStatus !== 'not_submitted' ? styles.dotActive : ''}`} />
                        <span>Submitted</span>
                      </div>
                      <div className={styles.pensionLine} />
                      <div className={styles.pensionStep}>
                        <span className={`${styles.pensionDot} ${['processing', 'approved', 'paid'].includes(selectedCase.pensionStatus) ? styles.dotActive : ''}`} />
                        <span>Processing</span>
                      </div>
                      <div className={styles.pensionLine} />
                      <div className={styles.pensionStep}>
                        <span className={`${styles.pensionDot} ${['approved', 'paid'].includes(selectedCase.pensionStatus) ? styles.dotActive : ''}`} />
                        <span>Approved</span>
                      </div>
                      <div className={styles.pensionLine} />
                      <div className={styles.pensionStep}>
                        <span className={`${styles.pensionDot} ${selectedCase.pensionStatus === 'paid' ? styles.dotActive : ''}`} />
                        <span>Paid</span>
                      </div>
                    </div>
                    {selectedCase.pensionReference && (
                      <p className={styles.pensionRef}>Reference: {selectedCase.pensionReference}</p>
                    )}
                  </div>

                  {/* Exit Clearance */}
                  <div className={styles.clearanceSection}>
                    <div className={styles.sectionHeader}>
                      <h3 className={styles.sectionTitle}>Exit Clearance</h3>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${getClearanceProgress()}%` }}
                        />
                        <span className={styles.progressText}>{getClearanceProgress()}%</span>
                      </div>
                    </div>
                    <div className={styles.clearanceItems}>
                      {clearance.map(item => (
                        <div
                          key={item.id}
                          className={`${styles.clearanceItem} ${styles[`clearance${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`]}`}
                        >
                          <div className={styles.clearanceIcon}>
                            {item.status === 'cleared' && '✓'}
                            {item.status === 'blocked' && '✗'}
                            {item.status === 'pending' && '○'}
                          </div>
                          <span className={styles.clearanceDept}>{item.departmentName}</span>
                          <span className={`${styles.clearanceStatus} ${styles[`status${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`]}`}>
                            {item.status}
                          </span>
                        </div>
                      ))}
                      {clearance.length === 0 && (
                        <p className={styles.noItems}>Clearance not initialized</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Create Case Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Create Retirement Case</h2>
              <button className={styles.modalClose} onClick={() => setShowCreateModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              {selectedStaff ? (
                <div className={styles.createCaseForm}>
                  <div className={styles.selectedStaffCard}>
                    <h4>{selectedStaff.firstName} {selectedStaff.lastName}</h4>
                    <p>{selectedStaff.employeeId} • {selectedStaff.departmentName}</p>
                    <p>Retirement: {formatDate(selectedStaff.expectedRetirementDate)}</p>
                  </div>
                  <p className={styles.formNote}>
                    This will create a retirement case and initialize the document checklist.
                  </p>
                  <div className={styles.modalActions}>
                    <button className={styles.cancelBtn} onClick={() => setSelectedStaff(null)}>
                      Select Different Staff
                    </button>
                    <button className={styles.createBtn}>
                      Create Case
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.staffSelection}>
                  <p className={styles.selectPrompt}>Select a staff member from the alerts to create a case</p>
                  <div className={styles.staffList}>
                    {approaching.filter(a => !a.hasActiveCase).map(staff => (
                      <div
                        key={staff.staffId}
                        className={styles.staffOption}
                        onClick={() => setSelectedStaff(staff)}
                      >
                        <span className={styles.staffOptionName}>
                          {staff.firstName} {staff.lastName}
                        </span>
                        <span className={styles.staffOptionMeta}>
                          {staff.employeeId} • {staff.daysUntilRetirement} days
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
